import React from 'react';
import { Target, Eye, Award, Users, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import './About.css';
import { technologies } from '../data/mock';

const About = () => {
  return (
    <div className="about-page">
      {/* Hero */}
      <section className="about-hero">
        <div className="container text-center">
          <h1 className="display-large mb-3 fade-in">
            Building the Future of <span className="gradient-text">Digital Excellence</span>
          </h1>
          <p className="body-large fade-in" style={{ animationDelay: '0.1s' }}>
            We're a team of innovators, problem-solvers, and digital experts committed to transforming businesses through cutting-edge technology.
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="section">
        <div className="container">
          <div className="mission-vision-grid">
            <div className="glass-card mission-card fade-in">
              <div className="card-icon">
                <Target size={40} />
              </div>
              <h2 className="heading-1 mb-2">Our Mission</h2>
              <p className="body-medium">
                To empower businesses with intelligent digital solutions that drive measurable growth, operational excellence, and competitive advantage. We believe in technology that serves people, not the other way around.
              </p>
            </div>
            <div className="glass-card vision-card fade-in" style={{ animationDelay: '0.15s' }}>
              <div className="card-icon">
                <Eye size={40} />
              </div>
              <h2 className="heading-1 mb-2">Our Vision</h2>
              <p className="body-medium">
                To be the world's most trusted digital transformation partner, known for delivering exceptional results, fostering innovation, and creating lasting value for our clients across all industries.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section why-section">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-medium mb-2">Why Choose Data Clouds</h2>
            <p className="body-large">What sets us apart in the digital landscape</p>
          </div>
          <div className="why-grid">
            <div className="why-card glass-card fade-in">
              <div className="why-icon">
                <Award size={32} />
              </div>
              <h3 className="heading-2 mb-2">Proven Expertise</h3>
              <p className="body-medium">
                500+ successful projects across diverse industries. Our team brings deep technical knowledge and real-world experience to every engagement.
              </p>
            </div>
            <div className="why-card glass-card fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="why-icon">
                <CheckCircle size={32} />
              </div>
              <h3 className="heading-2 mb-2">Results-Driven</h3>
              <p className="body-medium">
                We focus on outcomes, not just deliverables. Every solution is designed to generate measurable ROI and sustainable business growth.
              </p>
            </div>
            <div className="why-card glass-card fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="why-icon">
                <Users size={32} />
              </div>
              <h3 className="heading-2 mb-2">Client-First Approach</h3>
              <p className="body-medium">
                Your success is our success. We build long-term partnerships based on trust, transparency, and exceptional service.
              </p>
            </div>
            <div className="why-card glass-card fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="why-icon">
                <Target size={32} />
              </div>
              <h3 className="heading-2 mb-2">Cutting-Edge Technology</h3>
              <p className="body-medium">
                We stay ahead of the curve with the latest technologies, frameworks, and methodologies to deliver future-proof solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Image Section */}
      <section className="section team-section">
        <div className="container">
          <div className="team-content-grid">
            <div className="team-image-wrapper slide-in-left">
              <img 
                src="https://images.pexels.com/photos/3153207/pexels-photo-3153207.jpeg" 
                alt="Data Clouds Team" 
                className="team-image"
              />
            </div>
            <div className="team-text slide-in-right">
              <h2 className="display-medium mb-3">Meet Our Expert Team</h2>
              <p className="body-large mb-3">
                Our team of 50+ specialists includes senior developers, AI engineers, UX designers, and business strategists who are passionate about innovation.
              </p>
              <ul className="team-highlights">
                <li>
                  <CheckCircle size={20} className="highlight-icon" />
                  <span>15+ years average industry experience</span>
                </li>
                <li>
                  <CheckCircle size={20} className="highlight-icon" />
                  <span>Certified experts in leading technologies</span>
                </li>
                <li>
                  <CheckCircle size={20} className="highlight-icon" />
                  <span>Diverse skill sets across multiple domains</span>
                </li>
                <li>
                  <CheckCircle size={20} className="highlight-icon" />
                  <span>Committed to continuous learning and growth</span>
                </li>
              </ul>
              <Link to="/contact" className="btn-primary mt-4">
                Work With Us
                <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technologies */}
      <section className="section tech-section">
        <div className="container">
          <div className="section-header text-center mb-5">
            <h2 className="display-medium mb-2">Technologies We Master</h2>
            <p className="body-large">Building with the best tools in the industry</p>
          </div>
          <div className="tech-cloud">
            {technologies.map((tech, index) => (
              <span key={index} className="tech-badge fade-in" style={{ animationDelay: `${index * 0.05}s` }}>
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <div className="about-cta glass-card text-center">
            <h2 className="display-medium mb-3">Let's Build Something Amazing Together</h2>
            <p className="body-large mb-4">
              Ready to transform your business? Schedule a free consultation and let's discuss how we can help you achieve your goals.
            </p>
            <Link to="/contact" className="btn-primary btn-large">
              Get In Touch
              <ArrowRight size={22} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
